import { FontStyle } from '../../items/text/text-item-type';
import {
  FONT_FAMILY_LIST,
  FONT_SOURCE_MAP,
  FONT_WEIGHTS_BY_FAMILY_FALLBACK,
  FontFamilyValue,
  FontWeight,
  getFontDefaultWeight,
  getFontPathByWeight,
} from '@/features/remotion-editor/scripts/local-font-data';

// 서버/클라이언트 공통으로 사용할 로컬 폰트 메타데이터 계산 로직
const FALLBACK_FONT_FAMILY: FontFamilyValue = 'Pretendard';

export type LocalFontInfo = {
  family: string;
  sourcePath: string; // 기본 path (호환성 유지)
  defaultWeight: string; // 기본 weight
  weights: string[];
  getPathByWeight: (weight: string) => string; // weight별 path 반환
};

const fontInfoPromiseCache: Record<string, Promise<LocalFontInfo>> = {};

const toWeights = (fontFamily: FontFamilyValue): string[] => {
  const fallback = FONT_WEIGHTS_BY_FAMILY_FALLBACK[fontFamily];
  if (fallback && fallback.length > 0) {
    return fallback.map((weight) => String(weight));
  }
  return ['400'];
};

const resolveFontFamily = (fontFamily: string) => {
  const match = FONT_FAMILY_LIST.find(
    (font) =>
      font.value === fontFamily ||
      font.name === fontFamily ||
      font.key === fontFamily,
  );

  if (match) {
    return match;
  }

  return (
    FONT_FAMILY_LIST.find((font) => font.key === FALLBACK_FONT_FAMILY) ??
    FONT_FAMILY_LIST[0]!
  );
};

const createLocalFontInfo = (fontFamily: string): LocalFontInfo => {
  const match = resolveFontFamily(fontFamily);
  const source = FONT_SOURCE_MAP.get(match.value);

  if (!source) {
    throw new Error(`Font source not found for ${match.value}`);
  }

  const defaultWeight = getFontDefaultWeight(match.value);

  return {
    family: match.value,
    sourcePath: source.defaultPath,
    defaultWeight: String(defaultWeight),
    weights: toWeights(match.key as FontFamilyValue),
    getPathByWeight: (weight: string) => {
      const numWeight = parseInt(weight, 10) as FontWeight;
      return getFontPathByWeight(match.value, numWeight);
    },
  };
};

export const getFontVariants = (fontInfo: LocalFontInfo) => {
  const weights = fontInfo.weights;
  const styles: FontStyle[][] = [];

  styles.push(
    weights.map((weight) => ({
      variant: 'normal',
      weight,
    })),
  );

  return styles;
};

export const loadFontInfoFromApi = async (
  fontFamily: string,
): Promise<LocalFontInfo> => {
  if (!fontInfoPromiseCache[fontFamily]) {
    fontInfoPromiseCache[fontFamily] = Promise.resolve(
      createLocalFontInfo(fontFamily),
    );
  }

  return fontInfoPromiseCache[fontFamily];
};

export const buildLocalFontInfo = createLocalFontInfo;
